import { uploadFile } from '@/lib/files/file-uploads';

/**
 * Queue to manage concurrent file uploads. This ensures files are uploaded sequentially
 * to prevent race conditions and provide better error handling.
 */
const uploadQueue: Array<() => Promise<void>> = [];

/** Flag to track if the queue is currently being processed */
let isProcessingQueue = false;

/**
 * Processes the upload queue sequentially. This function ensures that only one upload
 * is processed at a time, preventing concurrent upload issues.
 *
 * The function will:
 * 1. Check if queue is already being processed
 * 2. Process each upload task in FIFO order
 * 3. Handle any errors without stopping the queue
 * 4. Reset the processing flag when done
 */
const processQueue = async () => {
  if (isProcessingQueue || uploadQueue.length === 0) {
    return;
  }

  isProcessingQueue = true;
  while (uploadQueue.length > 0) {
    const nextUpload = uploadQueue.shift();
    if (nextUpload) {
      try {
        await nextUpload();
      } catch (error) {
        console.error('Error processing upload:', error);
      }
    }
  }
  isProcessingQueue = false;
};

/**
 * Handles file upload with progress indication in the textarea.
 *
 * This function:
 * 1. Immediately shows a placeholder in the textarea for feedback
 * 2. Queues the actual upload operation
 * 3. Updates the textarea with the final URL or error message
 * 4. Maintains proper cursor position and text state
 *
 * @param file - The file to upload
 * @param textArea - The textarea element to update with upload status
 * @param insertPosition - Optional cursor position where to insert the upload status
 * @param onChange - Optional callback to notify of textarea value changes
 * @returns Promise resolving to the final textarea value
 */
export const handleFileUpload = async (
  file: File,
  textArea: HTMLTextAreaElement,
  repositoryId: number,
  insertPosition?: number,
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void,
) => {
  const placeholder = `![Uploading ${file.name}...]()`;
  let uploadPromiseResolve: (value: void) => void;
  let uploadPromiseReject: (reason?: any) => void;

  // Create a promise that will be resolved when the upload is complete
  const uploadPromise = new Promise<void>((resolve, reject) => {
    uploadPromiseResolve = resolve;
    uploadPromiseReject = reject;
  });

  // Insert placeholder at the correct position
  if (typeof insertPosition === 'number') {
    const before = textArea.value.substring(0, insertPosition);
    const after = textArea.value.substring(insertPosition);
    textArea.value = before + placeholder + after;
  } else {
    textArea.value += placeholder;
  }

  // Notify about the initial placeholder insertion
  onChange?.({ target: textArea } as React.ChangeEvent<HTMLTextAreaElement>);

  // Add upload task to queue
  uploadQueue.push(async () => {
    try {
      const url = await uploadFile(file, repositoryId);
      // Generate appropriate markdown tag based on file type
      const finalTag = file.type.startsWith('image/')
        ? `![${file.name}](${url})` // Image markdown
        : `[${file.name}](${url})`; // Regular link markdown (videos are treated as links on github, but rendered as images on cubic)

      textArea.value = textArea.value.replace(placeholder, finalTag);
      onChange?.({ target: textArea } as React.ChangeEvent<HTMLTextAreaElement>);
      uploadPromiseResolve();
    } catch (error) {
      console.error('Failed to upload file:', error);
      textArea.value = textArea.value.replace(placeholder, `[Failed to upload ${file.name}]`);
      onChange?.({ target: textArea } as React.ChangeEvent<HTMLTextAreaElement>);
      uploadPromiseReject(error);
    }
  });

  // Start processing queue
  processQueue();

  await uploadPromise;
  return textArea.value;
};

/**
 * Inserts a GIF into the textarea at the specified position.
 *
 * This function:
 * 1. Creates a markdown image tag for the GIF
 * 2. Inserts it at the specified position or appends it
 * 3. Maintains proper spacing with existing content
 *
 * @param gif - Object containing the GIF's URL and title
 * @param textArea - The textarea element to insert the GIF into
 * @param insertPosition - Optional cursor position where to insert the GIF
 * @param onChange - Optional callback to notify of textarea value changes
 * @returns The final textarea value
 */
export const insertGif = (
  gif: { url: string; title: string },
  textArea: HTMLTextAreaElement,
  insertPosition?: number,
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void,
) => {
  const gifTag = `![${gif.title}](${gif.url})`;

  if (typeof insertPosition === 'number') {
    const before = textArea.value.substring(0, insertPosition);
    const after = textArea.value.substring(insertPosition);
    textArea.value = before + gifTag + after;
  } else {
    // Add space before GIF if there's existing content
    textArea.value = textArea.value ? `${textArea.value} ${gifTag}` : gifTag;
  }

  onChange?.({ target: textArea } as React.ChangeEvent<HTMLTextAreaElement>);
  return textArea.value;
};
